// src/common/ui/footnote-cards.js
// Footnote card rendering utilities

import { truncate } from './dom-utils.js';
import { elTable, wrapMono } from './dom-utils.js';
import { renderAuditResults } from './citation-results.js';
import { processInlineMarkdown } from './page-renderer.js';
import { formatCitationType } from '../utils/type-formatter.js';
// Note: heading-groups.js imports createFootnoteCard from ../ui.js (not directly from this file)
// to avoid circular dependency issues. ES modules handle this correctly as long as
// the imports are used only inside functions (not at module initialization time).
import { createHierarchicalHeader, refreshAllHeadingStats } from './heading-groups.js';

/**
 * Create a footnote card element
 * @param {Object} fn - Footnote object
 * @param {Object} handlers - Event handlers { onNavigate, onVerify }
 * @returns {HTMLDivElement}
 */
export function createFootnoteCard(fn, handlers = {}) {
  const card = document.createElement('div');
  card.className = 'card';
  card.dataset.id = fn.id;

  const header = document.createElement('button');
  header.className = 'card-header';
  header.setAttribute('aria-expanded', 'false');

  // Navigation handler
  header.onclick = (ev) => {
    ev.stopPropagation();
    const bodyEl = card.querySelector('.card-body');
    const wasOpen = header.getAttribute('aria-expanded') === 'true';

    header.setAttribute('aria-expanded', String(!wasOpen));
    bodyEl.classList.toggle('show', !wasOpen);

    // If we just opened the card, call the onNavigate handler
    if (!wasOpen && handlers.onNavigate) {
      handlers.onNavigate(fn.id);
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  const chev = document.createElement('span');
  chev.className = 'chev';
  chev.textContent = '›';

  const title = document.createElement('span');
  title.className = 'card-title';
  
  // Create badge for footnote/inline citation
  const badge = document.createElement('span');
  badge.className = fn.is_inline ? 'badge badge-inline-citation' : 'badge badge-footnote';
  
  // Format the number shown to the user. Prefer label (the mark Word prints:
  // its counter skips custom-mark notes such as an author "*"), then
  // display_number (1-based document-reference order). fn.id is the OOXML w:id
  // used for DOM data-id / cache keys and is NOT a valid display number.
  let displayId = fn.label || ((fn.display_number != null) ? String(fn.display_number) : String(fn.id));
  if (fn.is_inline && displayId.startsWith('inline-')) {
    displayId = displayId.substring(7); // Remove "inline-" prefix
  }

  badge.textContent = fn.is_inline ? `InL. ${displayId}` : `Fn. ${displayId}`;
  
  // Clean up text (remove [MATH] and other markdown artifacts)
  let cleanText = (fn.text || '').replace(/\[MATH\]/g, '').trim();
  const textSpan = document.createElement('span');
  textSpan.textContent = cleanText ? ` ${truncate(cleanText, 90)}` : '';
  
  title.append(badge, textSpan);

  const sub = document.createElement('span');
  sub.className = 'card-sub';
  // Also clean up citation text
  let cleanCite = (fn.cite && typeof fn.cite === 'string') ? fn.cite.replace(/\[MATH\]/g, '').trim() : '';
  sub.textContent = cleanCite ? truncate(cleanCite, 60) : '';

  const spacer = document.createElement('span');
  spacer.className = 'spacer';

  const icons = document.createElement('span');
  icons.className = 'status-icons';
  icons.setAttribute('aria-label', 'Resolution status');

  header.append(chev, title, sub, spacer, icons);

  const body = document.createElement('div');
  body.className = 'card-body';

  // For inline citations, skip the info table (citation data shown below)
  if (!fn.is_inline) {
    // Convert markdown to HTML for display
    const fullTextHtml = fn.text ? processInlineMarkdown(fn.text) : '—';

    // Prefer the LLM-extracted claim once parsed; fall back to the raw context
    const claimText = fn.claim?.trim()
      || fn.cached_citations?.find((c) => c?.claim?.trim())?.claim
      || fn.parsed_citations?.find((c) => c?.claim?.trim())?.claim
      || '';
    const contextHtml = claimText
      ? processInlineMarkdown(claimText)
      : (fn.context ? processInlineMarkdown(fn.context) : '—');

    // Create divs with formatted HTML instead of monospace text
    const fullTextDiv = document.createElement('div');
    fullTextDiv.className = 'footnote-text';
    fullTextDiv.innerHTML = fullTextHtml;

    const contextDiv = document.createElement('div');
    contextDiv.className = 'footnote-context';
    contextDiv.innerHTML = contextHtml;

    const infoTable = elTable([
      ['Full text', fullTextDiv],
      [claimText ? 'Claim' : 'Context', contextDiv],
    ]);
    body.append(infoTable);
  }

  const results = document.createElement('div');
  results.className = 'results';
  
  // For inline citations, immediately render the parsed citation data
  if (fn.is_inline && fn.parsed_citations) {
    // Ensure file catalog is loaded to get source_url for cached citations (async, but don't block rendering)
    const mgr = window.footcheckFootnotesManager;
    if (mgr && mgr.fileCatalog && mgr.fileCatalog.size === 0) {
      // Load in background - will be available on next render if needed
      mgr.ensureFileCatalog().catch(err => {
        console.warn('[FOOTNOTE-CARDS] Failed to load file catalog:', err);
      });
    }
    
    // Import renderInitialCitationPlaceholders and renderCitationBlock from citation-results
    // But we'll do it inline here to avoid circular dependencies
    fn.parsed_citations.forEach(citation => {
      const block = document.createElement('div');
      block.className = 'citation-block';
      block.dataset.citeId = citation.id;
      
      // We need to manually create the simple view here
      const topRow = document.createElement('div');
      topRow.className = 'row';
      topRow.innerHTML = `<div><strong>Inline Citation</strong></div>`;
      
      const rows = [
        ['Citation', citation.source?.raw ?? '—'],
        ['Type', formatCitationType(citation.source?.type)],
      ];
      
      // Check for source URL from citation or from resolved file
      let sourceUrl = citation.source?.url_doi;
      
      // If citation was resolved and has a file_id, try to get source_url from file catalog
      const citationSourceDb = citation.source_db || citation.resolve_result?.source_db || 'doc';
      if (!sourceUrl && citation.file_id && citationSourceDb === 'doc') {
        if (mgr?.getFileSourceUrl) {
          sourceUrl = mgr.getFileSourceUrl(citation.file_id, citationSourceDb);
        } else if (mgr?.fileCatalog) {
          const fileInfo = mgr.fileCatalog.get(String(citation.file_id));
          sourceUrl = fileInfo?.source_url || null;
        }
      }
      
      // Also check cached_results for this citation if available
      if (!sourceUrl && fn.cached_results) {
        const cachedResult = fn.cached_results[String(citation.id)];
        if (cachedResult) {
          const fileId = cachedResult.file_id || cachedResult.file || cachedResult.resolve_result?.file_id;
          const cachedSourceDb = cachedResult.source_db || cachedResult.resolve_result?.source_db || 'doc';
          if (fileId && cachedSourceDb === 'doc') {
            if (mgr?.getFileSourceUrl) {
              sourceUrl = mgr.getFileSourceUrl(fileId, cachedSourceDb);
            } else if (mgr?.fileCatalog) {
              const fileInfo = mgr.fileCatalog.get(String(fileId));
              sourceUrl = fileInfo?.source_url || null;
            }
          }
        }
      }
      
      if (sourceUrl) {
        const link = document.createElement('a');
        link.href = sourceUrl;
        link.target = '_blank';
        link.textContent = 'Link';
        link.className = 'source-link';
        link.title = sourceUrl; // Full URL in tooltip
        
        // Add click handler for Tauri mode (only if not already attached)
        if (window.__TAURI__ && !link.dataset.handlerAttached) {
          link.dataset.handlerAttached = 'true';
          link.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation(); // Prevent event bubbling
            try {
              const { shell } = window.__TAURI__;
              await shell.open(sourceUrl);
            } catch (error) {
              console.error('Failed to open URL:', error);
              window.open(sourceUrl, '_blank');
            }
          }, { once: false, passive: false });
        }
        
        rows.push(['Source URL', link]);
      } else {
        rows.push(['Source URL', '—']);
      }
      
      // For inline citations, display the claim if available (LLM-extracted), otherwise fall back to context
      const hasClaim = !!(citation.claim && citation.claim.trim());
      const claimText = hasClaim
        ? citation.claim
        : (citation.context ?? fn.context ?? '—');

      // Format the claim text with markdown processing for better display
      const claimDiv = document.createElement('div');
      claimDiv.className = 'citation-claim';
      claimDiv.innerHTML = claimText !== '—' ? processInlineMarkdown(claimText) : '—';
      rows.push([hasClaim ? 'Claim' : 'Context', claimDiv]);
      
      const table = elTable(rows);
      
      block.append(topRow, table);
      results.append(block);
    });
  }
  
  body.append(results);

  // Add Verify/Check Again button below results for all footnotes (inline and regular)
  const actions = document.createElement('div');
  actions.className = 'row footnote-actions';
  const btn = document.createElement('button');
  // Change button text to "Check Again" if cached results exist
  btn.textContent = (fn.cached_results && Object.keys(fn.cached_results).length > 0) ? 'Check Again' : 'Verify';
  // Use the onVerify handler from the handlers object
  btn.onclick = (ev) => { ev.stopPropagation(); handlers.onVerify?.(fn.id, card); };
  actions.append(btn);
  body.append(actions);

  // Add a dedicated container for ask_user questions (below the verify button)
  const askContainer = document.createElement('div');
  askContainer.className = 'ask-container';
  body.append(askContainer);

  card.append(header, body);
  return card;
}

/**
 * Render multiple footnote cards, optionally grouped by headings
 * @param {HTMLElement} listEl - Container element
 * @param {Array} items - Array of footnote objects
 * @param {Array} headings - Optional array of heading objects
 * @param {Object} handlers - Event handlers
 */
export function renderFootnoteCards(listEl, items, headings = [], handlers = {}) {
  // Preserve the citation type filter panel if it exists
  const filterPanel = listEl.querySelector('.citation-type-filter-panel');
  
  // Clear the container
  listEl.innerHTML = '';
  
  // Re-add the filter panel at the top if it existed
  if (filterPanel) {
    listEl.prepend(filterPanel);
  }
  
  // Ensure headings is an array
  if (!Array.isArray(headings)) {
    headings = [];
  }
  
  console.log('[RENDER] renderFootnoteCards called with:', {
    itemsCount: items?.length || 0,
    headingsCount: headings?.length || 0,
    headings: headings,
    hasHandlers: !!handlers
  });
  
  // If no headings provided, render flat list (backward compatibility)
  if (!headings || headings.length === 0) {
    console.log('[RENDER] No headings, rendering flat list');
    items.forEach(fn => {
      const cardEl = createFootnoteCard(fn, handlers);
      listEl.append(cardEl);
      
      // Auto-render cached results if available
      const mgr = window.footcheckFootnotesManager;
      const footnoteId = String(fn.id);
      const cachedResults = mgr?.resultsCache?.getResults?.(footnoteId);
      const itemResults = fn.cached_results && typeof fn.cached_results === 'object'
        ? fn.cached_results
        : {};
      let resultsToRender = (cachedResults || itemResults) && {
        ...itemResults,
        ...(cachedResults && typeof cachedResults === 'object' ? cachedResults : {}),
      };
      
      // If we have cached_results on the item but not in resultsCache, store them now
      let finalResultsToRender = resultsToRender;
      if (!cachedResults && fn.cached_results && Object.keys(fn.cached_results).length > 0) {
        console.log(`[AUTO-RENDER] Storing cached_results for footnote ${footnoteId} from fn.cached_results`);
        mgr?.resultsCache?.storeResults?.(footnoteId, fn.cached_results);
        // Update resultsToRender to use the newly stored cached results
        const updatedResults = mgr?.resultsCache?.getResults?.(footnoteId);
        if (updatedResults) {
          finalResultsToRender = updatedResults;
        }
      }
      
      if (finalResultsToRender && Object.keys(finalResultsToRender).length > 0) {
        console.log(`[AUTO-RENDER] Rendering cached results for footnote ${footnoteId}:`, Object.keys(finalResultsToRender).length, 'entries');
        const resultsContainer = cardEl.querySelector('.results');
        console.log(`[AUTO-RENDER] Results container found:`, !!resultsContainer, 'for card:', cardEl);
        if (resultsContainer) {
          if (mgr && mgr.fileCatalog && mgr.fileCatalog.size === 0) {
            mgr.ensureFileCatalog().catch(err => {
              console.warn('[AUTO-RENDER] Failed to load file catalog:', err);
            });
          }
          
          const resolveFileDisplay = (data, cid) => {
            if (mgr?.getFileDisplay) {
              const result = mgr.getFileDisplay(data);
              if (result?.label) {
                const filename = result.label.split(/[\\/]/).pop();
                return { ...result, label: filename || result.label };
              }
              return result;
            }
            const fileId = data?.file_id || data?.file;
            const filePath = data?.file;
            let label = '—';
            if (filePath) {
              const filename = filePath.split(/[\\/]/).pop();
              label = filename || filePath;
            } else if (fileId && mgr?.fileCatalog && (data?.source_db || data?.resolve_result?.source_db || 'doc') === 'doc') {
              const fileInfo = mgr.fileCatalog.get(String(fileId));
              if (fileInfo?.path) {
                const filename = fileInfo.path.split(/[\\/]/).pop();
                label = filename || fileInfo.path;
              }
            }
            return { label };
          };
          
          const getFileSourceUrl = (fileId, sourceDb) => {
            if (mgr?.getFileSourceUrl) {
              return mgr.getFileSourceUrl(fileId, sourceDb);
            }
            return null;
          };
          
          console.log(`[AUTO-RENDER] Rendering for footnote ${footnoteId}, using container:`, listEl, 'cardEl:', cardEl);
          console.log(`[AUTO-RENDER] Card data-id:`, cardEl.dataset.id, 'fn.id:', fn.id);
          
          // Use setTimeout to ensure DOM is fully updated
          setTimeout(() => {
            renderAuditResults(listEl, fn.id, finalResultsToRender, {
              resolveFileDisplay,
              getFileSourceUrl,
              onChangeFile: (citeId) => {
                if (mgr?.reassignCitationFile) {
                  mgr.reassignCitationFile(fn.id, citeId);
                }
              },
              onResetCitation: (citeId, interaction) => {
                if (mgr?.resetCitationResult) {
                  mgr.resetCitationResult(fn.id, citeId, interaction);
                }
              },
            });
          }, 0);
          
          const verifyBtn = cardEl.querySelector('.card-body .footnote-actions button');
          if (verifyBtn) {
            verifyBtn.textContent = 'Check Again';
          }
        } else {
          console.warn(`[AUTO-RENDER] No results container found for footnote ${footnoteId}`);
        }
      } else {
        console.log(`[AUTO-RENDER] No cached results to render for footnote ${footnoteId}, cachedResults:`, !!cachedResults, 'fn.cached_results:', !!fn.cached_results);
      }
    });
    return;
  }
  
  // Group footnotes by headings
  console.log('[RENDER] Grouping footnotes by headings, headings:', headings);
  const headingMap = Object.fromEntries(headings.map(h => [h.id, h]));
  console.log('[RENDER] headingMap:', headingMap);
  
  const noHeadingItems = [];
  
  // Separate items with no heading
  for (const item of (items || [])) {
    if (!item.heading_id) {
      noHeadingItems.push(item);
    }
  }
  console.log('[RENDER] Items with no heading:', noHeadingItems.length);
  
  // Group items by heading
  const byHeading = {};
  for (const item of (items || [])) {
    if (item.heading_id) {
      (byHeading[item.heading_id] ||= []).push(item);
    }
  }
  console.log('[RENDER] Items by heading:', Object.keys(byHeading).length, 'headings with items');
  console.log('[RENDER] byHeading breakdown:', Object.entries(byHeading).map(([h, items]) => `${h}: ${items.length}`));
  
  // Render headings in document order, maintaining hierarchy
  // Track the stack of active parent content containers for nesting
  const parentStack = []; // Array of { level, contentEl } pairs
  
  for (const heading of (headings || [])) {
    const level = heading.level || 1;
    const groupItems = byHeading[heading.id] || [];
    
    console.log(`[RENDER] Processing heading "${heading.text}" (level ${level}) with ${groupItems.length} items`);
    
    // Pop parent stack until we find the appropriate parent level
    // A heading at level N should be nested under the most recent heading at level < N
    while (parentStack.length > 0 && parentStack[parentStack.length - 1].level >= level) {
      parentStack.pop();
    }
    
    // Get the parent content container (if any)
    const parentContent = parentStack.length > 0 ? parentStack[parentStack.length - 1].contentEl : null;
    
    // Create the heading header and content
    // Get mgr from handlers or window (for Word add-in compatibility)
    const mgr = handlers?.mgr || window.footcheckFootnotesManager;
    const { headerEl, contentEl } = createHierarchicalHeader(heading, groupItems, level, parentContent, {
      handlers,
      mgr,
      footnoteListEl: listEl
    });
    
    // Add to the appropriate parent or root
    if (parentContent) {
      // Nest under parent
      parentContent.appendChild(headerEl);
      parentContent.appendChild(contentEl);
    } else {
      // Add to root
      listEl.appendChild(headerEl);
      listEl.appendChild(contentEl);
    }
    
    // Push this heading onto the parent stack
    parentStack.push({ level, contentEl });
  }
  
  // Render no-heading items
  if (noHeadingItems.length > 0) {
    // Get mgr from handlers or window (for Word add-in compatibility)
    const mgr = handlers?.mgr || window.footcheckFootnotesManager;
    const { headerEl, contentEl } = createHierarchicalHeader(null, noHeadingItems, 0, null, {
      handlers,
      mgr,
      footnoteListEl: listEl
    });
    listEl.appendChild(headerEl);
    listEl.appendChild(contentEl);
  }

  // Refresh heading stats after DOM is fully rendered (in case of cached results)
  const effectiveMgr = handlers?.mgr || window.footcheckFootnotesManager;
  if (effectiveMgr?.resultsCache) {
    // Use multiple requestAnimationFrame to ensure all async DOM updates are complete
    // The auto-render in createHierarchicalHeader uses setTimeout(..., 0), so we need
    // to wait for those plus the subsequent DOM updates
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          refreshAllHeadingStats(listEl, effectiveMgr.resultsCache, items);
        }, 100);
      });
    });
  }
}

/**
 * Highlight a footnote in the list
 * @param {HTMLElement} listEl - Container element
 * @param {string|number} id - Footnote ID to highlight
 */
export function highlightFootnoteInList(listEl, id) {
  if (!id) return;
  const targetId = String(id);
  listEl.querySelectorAll(".card").forEach(card => {
    const header = card.querySelector(".card-header");
    const body   = card.querySelector(".card-body");
    const on = (card.dataset.id === targetId);
    header?.setAttribute("aria-expanded", String(on));
    body?.classList.toggle("show", on);
  });
  const target = listEl.querySelector(`.card[data-id="${CSS.escape ? CSS.escape(targetId) : targetId}"]`);
  if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
}

// Alias for backward compatibility
export const highlightFootnoteInWordList = highlightFootnoteInList;
